import React, { useState, useEffect, useRef } from "react";
import "./UpdateSettings.css";
import myAxios from "../AxiosUrl";

function UpdateSettings() {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [iosVersion, setIosVersion] = useState("");
  const [androidVersion, setAndroidVersion] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingVersion, setFetchingVersion] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [versionHistory, setVersionHistory] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationType, setNotificationType] = useState("both");
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);
  // Broadcast progress from /update-admin/notification-status. The trigger
  // endpoint now returns as soon as the job is accepted and keeps sending in
  // the background (29k+ tokens take minutes), so the modal polls for progress
  // instead of holding one request open until nginx times it out.
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);
  const [stats, setStats] = useState({
    lastUpdated: null,
    currentVersionAge: 0,
    lastNotificationPush: null,
    lastNotificationSMS: null,
  });

  useEffect(() => {
    fetchCurrentVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentVersion = async () => {
    try {
      setFetchingVersion(true);

      const response = await myAxios.get("/update-admin/details");

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        setCurrentVersion({
          iosVersion: data.iosVersion,
          androidVersion: data.androidVersion,
          forceUpdate: data.forceUpdate,
        });

        // Initialize form forceUpdate from current version
        setForceUpdate(data.forceUpdate || false);

        setStats({
          lastUpdated: data.updatedAt ? new Date(data.updatedAt) : null,
          currentVersionAge: data.daysSinceUpdate || 0,
          lastNotificationPush: data.lastNotificationSentPush,
          lastNotificationSMS: data.lastNotificationSentSMS,
        });
      } else {
        setCurrentVersion(null);
        setStats({
          lastUpdated: null,
          currentVersionAge: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching current version:", error);
      setMessage({
        text: "Error fetching version: " + error.message,
        type: "error",
      });
    } finally {
      setFetchingVersion(false);
    }
  };

  const handleCreateVersion = async (e) => {
    e.preventDefault();

    const versionRegex = /^\d+\.\d+\.\d+$/;

    if (!iosVersion && !androidVersion) {
      setMessage({
        text: "At least one version (iOS or Android) is required",
        type: "error",
      });
      return;
    }

    if (iosVersion && !versionRegex.test(iosVersion)) {
      setMessage({
        text: "Invalid iOS version format. Use: x.x.x (e.g., 1.0.38)",
        type: "error",
      });
      return;
    }

    if (androidVersion && !versionRegex.test(androidVersion)) {
      setMessage({
        text: "Invalid Android version format. Use: x.x.x (e.g., 1.0.39)",
        type: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await myAxios.post(`/update-admin/platform`, {
        iosVersion: iosVersion || null,
        androidVersion: androidVersion || null,
        forceUpdate: forceUpdate,
      });

      if (response.data && response.data.success) {
        let isLowerVersion = false;
        if (
          (iosVersion &&
            currentVersion?.iosVersion &&
            compareVersions(iosVersion, currentVersion.iosVersion) < 0) ||
          (androidVersion &&
            currentVersion?.androidVersion &&
            compareVersions(androidVersion, currentVersion.androidVersion) < 0)
        ) {
          isLowerVersion = true;
        }

        const successText = `Versions registered: ${
          iosVersion ? `iOS ${iosVersion}` : ""
        }${iosVersion && androidVersion ? ", " : ""}${
          androidVersion ? `Android ${androidVersion}` : ""
        }${isLowerVersion ? " (Lower version for testing)" : ""}`;

        setMessage({
          text: successText,
          type: isLowerVersion ? "warning" : "success",
        });

        const historyEntry = {
          id: Date.now(),
          previous: `iOS: ${currentVersion?.iosVersion || "N/A"}, Android: ${
            currentVersion?.androidVersion || "N/A"
          }`,
          new: `iOS: ${iosVersion || "N/A"}, Android: ${
            androidVersion || "N/A"
          }`,
          updatedAt: new Date(),
          updatedBy: localStorage.getItem("name") || "Admin",
        };

        setVersionHistory([historyEntry, ...versionHistory]);

        await fetchCurrentVersion();

        setIosVersion("");
        setAndroidVersion("");

        setTimeout(() => {
          setMessage({ text: "", type: "" });
        }, 5000);
      } else {
        setMessage({
          text: `Failed: ${response.data?.error || "Unknown error"}`,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error updating version:", error);
      setMessage({
        text: `Error: ${error.response?.data?.error || error.message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVersion = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      setMessage({
        text: "Delete functionality not implemented in backend yet",
        type: "warning",
      });
    } catch (error) {
      setMessage({
        text: `Error: ${error.message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchCurrentVersion();
    setMessage({
      text: "Refreshing version data...",
      type: "info",
    });
    setTimeout(() => {
      setMessage({ text: "", type: "" });
    }, 2000);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const summarizeJob = (job) => {
    let text =
      job.status === "done"
        ? "Notifications sent.\n\n"
        : `Notification job ${job.status}${job.error ? `: ${job.error}` : ""}\n\n`;
    if (job.type !== "sms") {
      text += `Push: ${job.push.sent}/${job.push.total} sent`;
      if (job.push.failed > 0) text += ` (${job.push.failed} failed)`;
      text += "\n";
    }
    if (job.type !== "push") {
      text += `SMS: ${job.sms.sent}/${job.sms.total} sent`;
      if (job.sms.failed > 0) {
        text += ` (${job.sms.failed} failed${job.sms.error ? `: ${job.sms.error}` : ""})`;
      }
    }
    return text;
  };

  // Returns true while the job is still running.
  const applyJob = (job) => {
    setJobStatus(job);
    if (!job) return false;

    if (job.status === "running") {
      setSendingNotifications(true);
      const parts = [];
      if (job.type !== "sms") parts.push(`push ${job.push.processed}/${job.push.total}`);
      if (job.type !== "push") parts.push(`SMS ${job.sms.processed}/${job.sms.total}`);
      setMessage({
        text: `Sending in the background… ${parts.join(", ")}. You can close this window; the job keeps running on the server.`,
        type: "info",
      });
      return true;
    }

    setSendingNotifications(false);
    const anyFailed = (job.push?.failed || 0) + (job.sms?.failed || 0) > 0;
    setMessage({
      text: summarizeJob(job),
      type: job.status === "done" ? (anyFailed ? "warning" : "success") : "error",
    });
    fetchCurrentVersion(); // refresh the "last sent" stamps
    return false;
  };

  const pollJobStatus = async () => {
    try {
      const response = await myAxios.get("/update-admin/notification-status");
      if (response.data?.success) {
        const running = applyJob(response.data.job);
        if (!running) stopPolling();
      }
    } catch (error) {
      // transient; keep polling
    }
  };

  const startPolling = () => {
    stopPolling();
    pollJobStatus();
    pollRef.current = setInterval(pollJobStatus, 3000);
  };

  // When the modal opens, pick up a broadcast that is already running (for
  // example after a page reload); stop polling when it closes.
  useEffect(() => {
    if (!showNotificationModal) {
      stopPolling();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await myAxios.get("/update-admin/notification-status");
        if (cancelled || !response.data?.success) return;
        if (response.data.job?.status === "running") {
          startPolling();
        } else {
          setJobStatus(response.data.job || null);
        }
      } catch (error) {
        // ignore; the Send button still works
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotificationModal]);

  useEffect(() => () => stopPolling(), []);

  const handleTriggerNotifications = async () => {
    setSendingNotifications(true);
    setNotificationResult(null);
    setMessage({ text: "", type: "" });

    try {
      const response = await myAxios.post("/update-admin/trigger-notifications", {
        notificationType: notificationType,
      });

      if (response.data?.success && response.data.accepted) {
        const totals = response.data.results || {};
        const parts = [];
        if (totals.push) parts.push(`${totals.push.total} push tokens`);
        if (totals.sms) parts.push(`${totals.sms.total} SMS numbers`);
        setMessage({
          text: `Accepted. Sending to ${parts.join(" and ")} in the background…`,
          type: "info",
        });
        startPolling();
      } else if (response.data?.success) {
        // Older backend that still sends synchronously.
        setNotificationResult(response.data.results);
        setSendingNotifications(false);
        setMessage({ text: "Notifications sent successfully!", type: "success" });
      } else {
        setSendingNotifications(false);
        setMessage({
          text: `Failed: ${response.data?.error || "Unknown error"}`,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error triggering notifications:", error);
      const status = error.response?.status;
      const errorMessage = error.response?.data?.error || error.message;
      if (status === 502 || status === 504) {
        // Gateway timed out but the child may well be running: show its progress.
        setMessage({
          text: "The server took too long to answer. Checking whether the job is running…",
          type: "warning",
        });
        startPolling();
      } else {
        setSendingNotifications(false);
        setMessage({ text: `Error: ${errorMessage}`, type: "error" });
      }
    }
  };

  const renderProgress = (label, data) => {
    const total = data?.total || 0;
    const processed = data?.processed || 0;
    const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    return (
      <div className="us-progress" key={label}>
        <div className="us-progress-label">
          <span>{label}</span>
          <span>
            {processed}/{total} processed · {data?.sent || 0} sent
            {data?.failed ? ` · ${data.failed} failed` : ""}
          </span>
        </div>
        <div className="us-progress-track">
          <div className="us-progress-bar" style={{ width: `${pct}%` }} />
        </div>
        {data?.error && <p className="us-warning-text">{data.error}</p>}
      </div>
    );
  };

  const compareVersions = (v1, v2) => {
    const parts1 = v1.split(".").map(Number);
    const parts2 = v2.split(".").map(Number);

    for (let i = 0; i < 3; i++) {
      if (parts1[i] > parts2[i]) return 1;
      if (parts1[i] < parts2[i]) return -1;
    }
    return 0;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Dhaka",
    });
  };

  return (
    <div className="us-container">
      {/* Header */}
      <div className="us-header">
        <div className="us-header-content">
          <div className="us-header-title">
            <span className="material-symbols-outlined">system_update</span>
            <div>
              <h1>App Version Management</h1>
              <p>Manage and track app version updates</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="us-btn-refresh"
            disabled={fetchingVersion}
          >
            <span className="material-symbols-outlined">refresh</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="us-stats">
        <div className="us-stat-card">
          <span className="material-symbols-outlined us-stat-icon">verified</span>
          <div>
            <p className="us-stat-label">iOS Version</p>
            <h3 className="us-stat-value">
              {fetchingVersion ? "..." : currentVersion?.iosVersion || "N/A"}
            </h3>
          </div>
        </div>

        <div className="us-stat-card">
          <span className="material-symbols-outlined us-stat-icon">android</span>
          <div>
            <p className="us-stat-label">Android Version</p>
            <h3 className="us-stat-value">
              {fetchingVersion ? "..." : currentVersion?.androidVersion || "N/A"}
            </h3>
          </div>
        </div>

        <div className="us-stat-card">
          <span className="material-symbols-outlined us-stat-icon">schedule</span>
          <div>
            <p className="us-stat-label">Last Updated</p>
            <h3 className="us-stat-value">
              {fetchingVersion ? "..." : stats.currentVersionAge === 0 ? "Today" : `${stats.currentVersionAge} days ago`}
            </h3>
          </div>
        </div>

        <div className="us-stat-card">
          <span className="material-symbols-outlined us-stat-icon">
            {currentVersion?.forceUpdate ? "priority_high" : "check_circle"}
          </span>
          <div>
            <p className="us-stat-label">Force Update</p>
            <h3 className="us-stat-value">
              {fetchingVersion ? "..." : currentVersion?.forceUpdate ? "Enabled" : "Disabled"}
            </h3>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="us-content">
        {/* Register New Version */}
        <div className="us-card">
          <div className="us-card-header">
            <h2>
              <span className="material-symbols-outlined">add_circle</span>
              Register New Version
            </h2>
          </div>

          <form onSubmit={handleCreateVersion} className="us-form">
            <div className="us-form-group">
              <label>iOS Version</label>
              <input
                type="text"
                value={iosVersion}
                onChange={(e) => setIosVersion(e.target.value)}
                placeholder="e.g., 1.0.38"
                disabled={loading || fetchingVersion}
              />
              <small>Format: MAJOR.MINOR.PATCH</small>
            </div>

            <div className="us-form-group">
              <label>Android Version</label>
              <input
                type="text"
                value={androidVersion}
                onChange={(e) => setAndroidVersion(e.target.value)}
                placeholder="e.g., 1.0.39"
                disabled={loading || fetchingVersion}
              />
              <small>Format: MAJOR.MINOR.PATCH</small>
            </div>

            <div className="us-detail-row us-force-update-row">
              <div className="us-force-update-info">
                <span>Force Update</span>
                <small>When enabled, users must update to continue using the app</small>
              </div>
              <label className="us-toggle">
                <input
                  type="checkbox"
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                  disabled={loading || fetchingVersion}
                />
                <span className="us-toggle-slider"></span>
              </label>
            </div>

            {message.text && (
              <div className={`us-message us-message-${message.type}`}>
                {message.text}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || fetchingVersion || (!iosVersion && !androidVersion)}
              className="us-btn us-btn-primary"
            >
              {loading ? "Registering..." : "Register Version"}
            </button>
          </form>
        </div>

        {/* Current Version Details */}
        <div className="us-card">
          <div className="us-card-header">
            <h2>
              <span className="material-symbols-outlined">info</span>
              Current Version Details
            </h2>
          </div>

          {fetchingVersion ? (
            <div className="us-loading">Loading version data...</div>
          ) : currentVersion ? (
            <div className="us-details">
              <div className="us-detail-row">
                <span>iOS Version</span>
                <span className="us-version-badge">{currentVersion.iosVersion || "N/A"}</span>
              </div>

              <div className="us-detail-row">
                <span>Android Version</span>
                <span className="us-version-badge">{currentVersion.androidVersion || "N/A"}</span>
              </div>

              <div className="us-detail-row">
                <span>Last Updated</span>
                <span>{formatDate(stats.lastUpdated)}</span>
              </div>

              <div className="us-detail-row">
                <span>Status</span>
                <span className="us-badge-success">Active</span>
              </div>

              <div className="us-detail-row">
                <span>Force Update</span>
                <span className={currentVersion?.forceUpdate ? "us-badge-danger" : "us-badge-success"}>
                  {currentVersion?.forceUpdate ? "Enabled" : "Disabled"}
                </span>
              </div>

              <div className="us-detail-actions">
                <button
                  className="us-btn us-btn-primary"
                  onClick={() => setShowNotificationModal(true)}
                  disabled={loading}
                >
                  Send Notifications
                </button>
                <button
                  className="us-btn us-btn-danger-outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                >
                  Delete Version
                </button>
              </div>
            </div>
          ) : (
            <div className="us-empty">
              <span className="material-symbols-outlined">inbox</span>
              <p>No version registered yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Version History */}
      <div className="us-card us-card-full">
        <div className="us-card-header">
          <h2>
            <span className="material-symbols-outlined">history</span>
            Version History
          </h2>
          <span className="us-badge-count">{versionHistory.length}</span>
        </div>

        {versionHistory.length > 0 ? (
          <div className="us-history-list">
            {versionHistory.map((entry) => (
              <div key={entry.id} className="us-history-item">
                <span className="material-symbols-outlined us-history-icon">upgrade</span>
                <div className="us-history-content">
                  <div className="us-history-versions">
                    <span className="us-version-old">{entry.previous}</span>
                    <span className="material-symbols-outlined">arrow_forward</span>
                    <span className="us-version-new">{entry.new}</span>
                  </div>
                  <div className="us-history-meta">
                    <span>{entry.updatedBy}</span>
                    <span>{formatDate(entry.updatedAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="us-empty">
            <span className="material-symbols-outlined">history</span>
            <p>No version history yet</p>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="us-card us-card-full">
        <div className="us-card-header">
          <h2>
            <span className="material-symbols-outlined">help</span>
            How It Works
          </h2>
        </div>

        <div className="us-info-grid">
          <div className="us-info-section">
            <h3>Mobile App Behavior</h3>
            <ul>
              <li>App checks for updates every 24 hours automatically</li>
              <li>Users with older versions see the update screen</li>
              <li>Update check runs on app launch (if 24 hours passed)</li>
            </ul>
          </div>

          <div className="us-info-section">
            <h3>Admin Actions</h3>
            <ul>
              <li>Register new version using semantic versioning (x.x.x)</li>
              <li>Only ONE version record is maintained in database</li>
              <li>New version must be greater than current version</li>
            </ul>
          </div>

          <div className="us-info-section">
            <h3>Security</h3>
            <ul>
              <li>Endpoint /app-version-check is public (for mobile apps)</li>
              <li>Version comparison uses semantic versioning rules</li>
              <li>Automatic cleanup of duplicate version records</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="us-modal-overlay" onClick={() => !sendingNotifications && setShowNotificationModal(false)}>
          <div className="us-modal" onClick={(e) => e.stopPropagation()}>
            <div className="us-modal-header">
              <span className="material-symbols-outlined">notifications_active</span>
              <h3>Send Update Notifications</h3>
            </div>
            <div className="us-modal-body">
              <p>
                Send update notifications to all users for version:
                <br />
                <strong>
                  iOS: {currentVersion?.iosVersion || "N/A"} | Android: {currentVersion?.androidVersion || "N/A"}
                </strong>
              </p>

              <div className="us-form-group">
                <label>Notification Type</label>
                <select
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value)}
                  disabled={sendingNotifications}
                >
                  <option value="both">Push + SMS (Both)</option>
                  <option value="push">Push Notifications Only</option>
                  <option value="sms">SMS Only</option>
                </select>
              </div>

              <div className="us-info-box">
                Cooldown: 1 hour between notifications to prevent spam
              </div>

              {message.text && (
                <div className={`us-message us-message-${message.type}`}>
                  {message.text}
                </div>
              )}

              {notificationResult && (
                <div className="us-results">
                  <h4>Results:</h4>
                  {notificationResult.push && (
                    <p>Push: {notificationResult.push.sent}/{notificationResult.push.total} sent</p>
                  )}
                  {notificationResult.sms && (
                    <p>SMS: {notificationResult.sms.sent}/{notificationResult.sms.total} sent</p>
                  )}
                </div>
              )}

              {jobStatus && (
                <div className="us-results">
                  <h4>
                    {jobStatus.status === "running" ? "Progress" : "Last run"}
                    {jobStatus.dryRun ? " (dry run)" : ""}
                  </h4>
                  {jobStatus.type !== "sms" && renderProgress("Push", jobStatus.push)}
                  {jobStatus.type !== "push" && renderProgress("SMS", jobStatus.sms)}
                  <p className="us-job-meta">
                    {jobStatus.startedAt
                      ? `Started ${new Date(jobStatus.startedAt).toLocaleString()}`
                      : ""}
                    {jobStatus.finishedAt
                      ? ` · finished ${new Date(jobStatus.finishedAt).toLocaleString()}`
                      : ""}
                    {jobStatus.status === "failed" && jobStatus.error
                      ? ` · ${jobStatus.error}`
                      : ""}
                  </p>
                </div>
              )}
            </div>
            <div className="us-modal-actions">
              <button
                className="us-btn us-btn-secondary"
                onClick={() => setShowNotificationModal(false)}
              >
                {sendingNotifications ? "Close (keeps running)" : "Close"}
              </button>
              <button
                className="us-btn us-btn-primary"
                onClick={handleTriggerNotifications}
                disabled={sendingNotifications}
              >
                {sendingNotifications ? "Sending..." : "Send Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="us-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="us-modal" onClick={(e) => e.stopPropagation()}>
            <div className="us-modal-header us-modal-warning">
              <span className="material-symbols-outlined">warning</span>
              <h3>Delete Version?</h3>
            </div>
            <div className="us-modal-body">
              <p>Are you sure you want to delete the current version?</p>
              <div className="us-warning-text">
                Note: Delete functionality is not yet implemented in the backend.
              </div>
            </div>
            <div className="us-modal-actions">
              <button
                className="us-btn us-btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="us-btn us-btn-danger"
                onClick={handleDeleteVersion}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateSettings;
